<template>
  <div v-if="isConfigLoading" class="app-loading">
    <div class="app-loading__spinner" />
  </div>
  <Maintenance v-else-if="isMaintenanceMode" />
  <v-app v-else id="app-container" :class="sudoMode ? 'pt-8' : ''">
    <SudoModeBanner v-if="sudoMode" />
    <vue3-confirm-dialog />
    <Navigation />
    <NewsTicker :items="newsItems" v-if="false" />
    <div id="page">
      <router-view :key="$route.fullPath" />
    </div>
    <Contact v-if="!isOperator" />
    <PageFooter />
    <v-snackbar v-model="exceptionNotifier" vertical>
      <div class="text-subtitle-1 pb-2">
        {{ $t('OPERATORS.EXCEPTIONS.HEADLINE') }}
      </div>

      <p class="mb-4">
        {{ $t('OPERATORS.EXCEPTIONS.DESCRIPTION') }}
      </p>

      <p>
        {{ exceptionMessage }}
      </p>
      <template v-slot:actions>
        <v-btn color="red" variant="text" @click="exceptionNotifier = false">
          {{ $t('OPERATORS.EXCEPTIONS.CLOSE') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script lang="ts">
  import { defineComponent, ref } from 'vue';
  import { RouterView } from 'vue-router';
  import { $t, $tm } from '@/plugins/i18n';
  import { FEDERATED_SERVICE_ID, FEDERATED_TOKENS } from '@/data/cognito';

  import Stores from '@/stores';

  import Navigation from '@/components/page/Navigation.vue';
  import Contact from '@/components/modals/Contact.vue';
  import PageFooter from '@/components/page/Footer.vue';
  import Login from '@/services/Login';
  import Messages from './services/Messages';

  // global styles an ui options
  import '@fortawesome/fontawesome-pro/css/all.css';
  import '@fortawesome/fontawesome-pro/js/all.js';
  import '@vuepic/vue-datepicker/dist/main.css';
  import '@/scss/app.scss';
  import Profile from './services/Profile';
  import Partner from '@/services/Partner';
  import Substitute from '@/services/Substitute';

  import NewsItem from '@/definitions/interfaces/locales/NewsItem.i';
  import NewsTicker from '@/components/content/NewsTicker.vue';
  import Maintenance from '@/components/content/Maintenance.vue';

  import SudoModeBanner from '@/components/content/SudoModeBanner.vue';
  import Config from './services/Config';
  import EventBus from '@/services/EventBus';

  export default defineComponent({
    name: 'App',
    components: {
      RouterView,
      Navigation,
      Contact,
      PageFooter,
      NewsTicker,
      Maintenance,
      SudoModeBanner,
    },
    setup() {
      // initialize stores on app start
      // this needs to be done in a setup-function, otherwise pinia doesn't work
      Stores.init();

      const { signedIn, userType } = Stores.userDataStoreToRefs();
      const { isOperator } = Stores.profileDataStoreToRefs();

      const isMaintenanceMode = ref<boolean>(false);
      const isConfigLoading = ref<boolean>(true);
      const configPollingInterval = ref<number | null>(null);

      return {
        signedIn,
        userType,
        isOperator,
        operatorDataStore: Stores.operatorData,
        isMaintenanceMode,
        isConfigLoading,
        configPollingInterval,
        exceptionNotifier: ref<boolean>(false),
        exceptionMessage: ref<string>(''),

        newsItems: $tm('NEWS_TICKER.ITEMS') as NewsItem[],
      };
    },
    async mounted() {
      await this.checkConfig();
      this.isConfigLoading = false;
      // this.startConfigPolling();

      EventBus.on(EventBus.keys.SHOW_EXCEPTION, (exceptionData: any): void => {
        const errorCodeMatch = exceptionData.match(/\[([A-Z_]+)\]/);
        if (errorCodeMatch) {
          const errorCode = errorCodeMatch[1];
          if (errorCode === 'OVERLAPPING_SERVICE') {
            const key = this.isOperator ? 'OPERATORS.EXCEPTIONS.OVERLAPPING_SERVICE_OPERATOR' : 'OPERATORS.EXCEPTIONS.OVERLAPPING_SERVICE_SUBSTITUTE';
            this.exceptionMessage = $t(key);
          } else {
            const translationKey = `OPERATORS.EXCEPTIONS.${errorCode}`;
            const translated = $t(translationKey);
            this.exceptionMessage = translated !== translationKey ? translated : exceptionData;
          }
        } else {
          this.exceptionMessage = exceptionData;
        }
        this.exceptionNotifier = true;
      });
    },
    async created() {
      const federatedServiceId = localStorage.getItem(FEDERATED_SERVICE_ID);
      if (federatedServiceId) {
        const federatedTokens = localStorage.getItem(FEDERATED_TOKENS);
        await Login.fetchFederatedUser(federatedTokens);
        return;
      } else {
        await Login.fetchUser();
      }

      if (this.signedIn && !this.isOperator) {
        await Profile.fetchProfile();
        if (Stores.profileData.isPartner) {
          if (Stores.partnerData.services.length === 0) {
            Partner.fetchPartner();
          }
        } else if (Stores.profileData.isSubstitute) {
          if (Stores.substituteData.assignments.length === 0) {
            Substitute.fetchSubstitute(true);
          }
        }
        Messages.fetchMessages();
      }
    },
    computed: {
      sudoMode(): boolean {
        return this.userType === 'operator' && this.operatorDataStore.isImpersonating;
      },
    },
    // beforeUnmount() {
    //   this.stopConfigPolling();
    // },
    methods: {
      async checkConfig() {
        try {
          const config = await Config.getConfig();
          this.isMaintenanceMode = config.maintenance_mode;
        } catch (error) {
          console.error('Failed to fetch config:', error);
        }
      },
      startConfigPolling() {
        // Poll every 15 seconds
        this.configPollingInterval = window.setInterval(() => {
          this.checkConfig();
        }, 15000);
      },
      stopConfigPolling() {
        if (this.configPollingInterval) {
          clearInterval(this.configPollingInterval);
          this.configPollingInterval = null;
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  @import '@/scss/configs/colors';
  .app-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(180deg, rgba(247, 248, 250, 0.9), #fff);
  }

  .app-loading__spinner {
    width: 42px;
    height: 42px;
    border: 4px solid rgba(0, 0, 0, 0.08);
    border-top-color: $color-marine;
    border-radius: 50%;
    animation: app-spin 0.9s linear infinite;
  }

  @keyframes app-spin {
    to {
      transform: rotate(360deg);
    }
  }
  #app-container {
    min-width: 400px;
  }
</style>
